import { useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useAuth } from '../../context';
import {
  NOTIFICATION_CATEGORIES,
  getDefaultCategoriesForRoles,
  getNotificationCategory,
} from '../../constants/notificationCategories';
import {
  inboxCategoryFilterAtom,
  notificationsAtom,
  notificationPreferencesAtom,
} from '../../store/jotai';

export function InboxCategoryTabs() {
  const { user } = useAuth();
  const [activeCategory, setActiveCategory] = useAtom(inboxCategoryFilterAtom);
  const preferences = useAtomValue(notificationPreferencesAtom);
  const notifications = useAtomValue(notificationsAtom);

  const userRoles = user?.roles ?? [];
  const availableCategoryIds = getDefaultCategoriesForRoles(userRoles);

  const visibleCategories = NOTIFICATION_CATEGORIES.filter(
    (c) =>
      availableCategoryIds.includes(c.id) &&
      preferences?.categoryEnabled?.[c.id] !== false
  );

  const unreadByCategory = useMemo(() => {
    const counts: Record<string, number> = { all: 0 };
    for (const n of notifications) {
      if (n.readAt || n.archivedAt) continue;
      const cat = getNotificationCategory(n.notificationType);
      counts[cat] = (counts[cat] || 0) + 1;
      counts.all += 1;
    }
    return counts;
  }, [notifications]);

  const tabs = [
    { id: 'all' as const, label: 'All' },
    ...visibleCategories.map((c) => ({ id: c.id, label: c.label })),
  ];

  return (
    <div className='flex flex-wrap gap-2'>
      {tabs.map((tab) => {
        const isActive = activeCategory === tab.id;
        const count = unreadByCategory[tab.id] || 0;

        return (
          <button
            key={tab.id}
            type='button'
            onClick={() => setActiveCategory(tab.id)}
            className={`
              inline-flex items-center gap-1.5
              pl-3 ${count > 0 ? 'pr-2' : 'pr-3'} py-1.5
              text-sm font-medium rounded-full
              transition-all duration-150 whitespace-nowrap
              ${
                isActive
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-gray-600 bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:border-gray-300'
              }
            `}
          >
            {tab.label}
            {count > 0 && (
              <span
                className={`
                  inline-flex items-center justify-center
                  h-5 min-w-[20px] px-1.5
                  text-xs font-semibold rounded-full
                  ${
                    isActive
                      ? 'bg-white/25 text-white'
                      : 'bg-primary/10 text-primary'
                  }
                `}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
